// components/CoursePlanner/index.tsx
import { useState, useRef, useCallback } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { MoreVertical, Sparkles, Loader2, X, ClipboardCheck } from 'lucide-react';
import {
  useSuggestions,
  CHATBOT_ROUTED_MODES,
  DEFAULT_OPTIMIZER_MODE,
  OPTIMIZER_LABELS,
} from '../../contexts/SuggestionContext';
import PlanAdvisories from './PlanAdvisories';
import { useModalOperations } from '../../contexts/ModalOperationsContext';
import { typedApi } from '../../services/typedApiClient';
import { dispatchDegreeAuditRefresh, dispatchAskChatbotHelp } from '../../utils/appEvents';
import SemesterGrid from './SemesterGrid';
import PlanTimeline from './PlanTimeline';
import BookmarkedCourses from './BookmarkedCourses';
import type { AcademicPlan, EnrolledCourse } from '../../types';

interface CoursePlannerProps {
  academicPlan: AcademicPlan | null;
  loading: boolean;
  collapsedSemesters: Record<string, boolean>;
  bookmarkedCollapsed: boolean;
  showBookmarkedCourses?: boolean;
  onToggleBookmarks: () => void;
  openDropdownId: string | null;
  setOpenDropdownId: (id: string | null) => void;
  dropdownContext?: string;
  onClearAllCourses?: (options?: { includePlaceholders?: boolean; skipConfirm?: boolean }) => void;
  showPriorCredits?: boolean;
  onTogglePriorCredits?: () => void;
  onApplyTemplate?: (distributionPlan: Record<string, EnrolledCourse[]>) => Promise<void>;
}

const dispatchChatbotHelp = (message: string, action: string): void => {
  dispatchAskChatbotHelp({ message, action });
};

const getCurrentSemester = (): string => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();

  if (month >= 1 && month <= 5) return `spring-${year}`;
  if (month >= 6 && month <= 8) return `summer-${year}`;
  return `fall-${year}`;
};

const CoursePlanner = ({
  academicPlan,
  loading,
  collapsedSemesters,
  bookmarkedCollapsed,
  showBookmarkedCourses = true,
  onToggleBookmarks,
  openDropdownId,
  setOpenDropdownId,
  dropdownContext,
  onClearAllCourses,
}: CoursePlannerProps) => {
  const {
    optimizationStatus,
    isGenerating,
    optimizerMode,
    onGenerateSuggestions,
  } = useSuggestions();

  const { onManageSemesters } = useModalOperations();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [clearPlaceholdersToo, setClearPlaceholdersToo] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Progress marker (Phase 4d): the plan's declared "current term" drives the
  // highlight + completion derivation; wall-clock is only the fallback for
  // plans without a marker. Optimistic local state mirrors the credit-target
  // pattern (the plan object refreshes on the next full load).
  const [optimisticCurrentTerm, setOptimisticCurrentTerm] = useState<number | null>(null);
  const markerTerm = optimisticCurrentTerm ?? academicPlan?.current_term ?? null;
  const currentSemester = markerTerm != null ? String(markerTerm) : getCurrentSemester();

  const handleCurrentTermChange = useCallback(
    async (term: number): Promise<void> => {
      if (!academicPlan?.id) return;
      setOptimisticCurrentTerm(term);
      try {
        await typedApi.academicPlans.updateCurrentTerm(Number(academicPlan.id), term);
        // The marker changes completion derivation → refresh the audit.
        dispatchDegreeAuditRefresh(Number(academicPlan.id));
      } catch (err) {
        console.error('Error updating current term:', err);
        setOptimisticCurrentTerm(null);
      }
    },
    [academicPlan?.id]
  );

  useClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  if (!academicPlan) {
    return <div>Loading...</div>;
  }

  // Placeholders survive a clear by default (#205 — they encode intent);
  // the confirmation dialog offers to remove them too when any exist.
  const placeholderCount = Object.values(academicPlan?.semesters ?? {}).reduce(
    (n, courses) =>
      n + (Array.isArray(courses) ? courses.filter((c) => c.isPlaceholder).length : 0),
    0
  );

  const handleClearAll = () => {
    if (onClearAllCourses) {
      // This dialog IS the confirmation — skip the hook's own confirm.
      onClearAllCourses({ includePlaceholders: clearPlaceholdersToo, skipConfirm: true });
      setShowClearConfirmation(false);
      setClearPlaceholdersToo(false);
    }
  };

  const askChatbotToSuggest = () => dispatchChatbotHelp(
    'Generate course suggestions for my plan.',
    'suggest_courses'
  );

  const askChatbotToAudit = () => dispatchChatbotHelp(
    'Audit my academic plan for correctness, completeness, and quality.',
    'audit_plan'
  );

  const askChatbotToClear = () => dispatchChatbotHelp(
    'Clear all course suggestions from my plan.',
    'clear_suggestions'
  );

  return (
    <div>
      {/* UI Tier 3.3b: unboxed column header — semester cards sit directly on
          the warm ground with one gap rhythm (no panel-in-panel nesting). */}
      <div className="px-1 pb-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-ink">Academic Plan</h2>
          <div className="flex items-center gap-2">
            {academicPlan?.gpa?.cumulative_gpa?.current != null && (
              <div className="text-xs flex items-center gap-2">
                <span className="font-medium text-primary-strong">
                  GPA {academicPlan.gpa.cumulative_gpa.current.toFixed(2)}
                </span>
                {academicPlan.gpa.cumulative_gpa.projected != null &&
                  academicPlan.gpa.cumulative_gpa.projected !== academicPlan.gpa.cumulative_gpa.current && (
                  <span className="text-ink-3" title="Projected GPA (including future grades)">
                    {academicPlan.gpa.cumulative_gpa.projected.toFixed(2)} projected
                  </span>
                )}
              </div>
            )}
            {/* Dropdown Menu */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="p-1.5 md:p-2 text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                title="Plan actions"
              >
                <MoreVertical className="h-4 w-4" />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      onManageSemesters();
                    }}
                    className="w-full px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 text-left"
                  >
                    Manage Semesters
                  </button>

                  {onClearAllCourses && (
                    <button
                      onClick={() => {
                        setIsDropdownOpen(false);
                        setShowClearConfirmation(true);
                      }}
                      className="w-full px-4 py-2 text-sm text-status-critical-fg hover:bg-status-critical/10 text-left"
                    >
                      Clear All Courses
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
            {/* Audit Plan button */}
            <button
              onClick={askChatbotToAudit}
              aria-label="Audit Plan"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-control transition-colors text-ink bg-surface-card border border-line-soft hover:bg-surface-sunken"
              title="Audit your plan for prerequisite violations, missing requirements, and load balance"
            >
              <ClipboardCheck className="h-3.5 w-3.5" />
              <span className="sm:hidden">Audit</span>
              <span className="hidden sm:inline">Audit Plan</span>
            </button>

            {/* Suggest Courses button */}
            <button
              onClick={CHATBOT_ROUTED_MODES.includes(optimizerMode)
                ? askChatbotToSuggest
                : onGenerateSuggestions}
              disabled={isGenerating || optimizationStatus?.state === 'computing'}
              aria-label={
                isGenerating || optimizationStatus?.state === 'computing'
                  ? 'Generating...'
                  : 'Suggest Courses'
              }
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-control transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-status-suggested-fg bg-status-suggested/10 border border-status-suggested/25 hover:bg-status-suggested/20"
              title={`${OPTIMIZER_LABELS[optimizerMode]} optimizer (Cmd+Shift+O to switch)`}
            >
              {isGenerating || optimizationStatus?.state === 'computing' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              <span className="sm:hidden">
                {isGenerating || optimizationStatus?.state === 'computing' ? 'Generating...' : 'Suggest'}
              </span>
              <span className="hidden sm:inline">
                {isGenerating || optimizationStatus?.state === 'computing'
                  ? 'Generating...'
                  : optimizerMode !== DEFAULT_OPTIMIZER_MODE
                    ? `Suggest Courses (${optimizerMode})`
                    : 'Suggest Courses'}
              </span>
            </button>

            {/* Clear Suggestions button - only when suggestions are active */}
            {(optimizationStatus?.state === 'complete' || optimizationStatus?.state === 'stale') && (
              <button
                onClick={askChatbotToClear}
                aria-label="Clear Suggestions"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ink-2 bg-surface-card border border-line-soft rounded-control hover:bg-surface-sunken transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                <span className="sm:hidden">Clear</span>
                <span className="hidden sm:inline">Clear Suggestions</span>
              </button>
            )}
        </div>
      </div>

      {/* Plan-level optimizer advisories (Phase 2.2/3.3 warning rendering) */}
      <PlanAdvisories recommendations={optimizationStatus?.recommendations} />

      {/* Content Section */}
      <div>
        <PlanTimeline
          semesters={academicPlan?.semesters}
          currentSemester={currentSemester}
          onCurrentTermChange={handleCurrentTermChange}
        />
        <SemesterGrid
          loading={loading}
          academicPlan={academicPlan}
          collapsedSemesters={collapsedSemesters}
          openDropdownId={openDropdownId}
          setOpenDropdownId={setOpenDropdownId}
          dropdownContext={dropdownContext}
          currentSemester={currentSemester}
        />

        {showBookmarkedCourses && (
          <div className="mt-4 md:mt-6">
            <BookmarkedCourses
              loading={loading}
              bookmarkedCourses={academicPlan.bookmarkedCourses}
              bookmarkedCollapsed={bookmarkedCollapsed}
              onToggleBookmarks={onToggleBookmarks}
              openDropdownId={openDropdownId}
              setOpenDropdownId={setOpenDropdownId}
              dropdownContext={dropdownContext}
              academicPlan={academicPlan}
            />
          </div>
        )}
      </div>

      {/* Clear Confirmation Modal */}
      {showClearConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Clear All Courses?
              </h3>
              <p className="text-gray-600 mb-4">
                This will remove all courses from your academic plan.
                Bookmarked courses and prior credits will be preserved. This action cannot be undone.
              </p>
              {placeholderCount > 0 && (
                <label className="flex items-start gap-2 mb-6 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={clearPlaceholdersToo}
                    onChange={(e) => setClearPlaceholdersToo(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Also remove {placeholderCount === 1
                      ? 'the 1 placeholder course'
                      : `all ${placeholderCount} placeholder courses`}.
                    Unchecked, placeholders stay in your plan.
                  </span>
                </label>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowClearConfirmation(false);
                    // Never leave the destructive option pre-selected for the
                    // next open (round-3 review nit).
                    setClearPlaceholdersToo(false);
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearAll}
                  className="px-4 py-2 text-white bg-status-critical-strong rounded-md hover:bg-status-critical-strong/90"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CoursePlanner;
